import { useEffect, useState } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { Icons } from './icons.tsx';

async function post(url: string, body: unknown) {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? `Request failed (${r.status})`);
  return j;
}

/** Shown instead of the editor when the server has accounts and nobody is signed in. */
export function SignIn({ onDone, sso }: { onDone: () => void; sso?: { name: string; only: boolean } | null }) {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [error, setError] = useState<string | null>(() => new URLSearchParams(location.search).get('signin_error'));
  const [busy, setBusy] = useState(false);
  return (
    <div className="signin">
      <form
        className="signin-card"
        onSubmit={async (e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          setBusy(true);
          setError(null);
          try {
            await post(`/api/auth/${mode}`, { email: f.get('email'), password: f.get('password'), name: f.get('name') });
            onDone();
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="signin-logo">{Icons.logo}</div>
        <h1>{mode === 'login' ? 'Sign in to Vellum' : 'Create your account'}</h1>
        {sso && (
          <a className="btn primary" href={`/api/auth/oidc/start?next=${encodeURIComponent(location.pathname + location.search.replace(/[?&]signin_error=[^&]*/, ''))}`}>
            Continue with {sso.name}
          </a>
        )}
        {sso?.only ? (
          error && <div className="signin-error">{error}</div>
        ) : (
          <>
        {sso && <div className="signin-or">or</div>}
        {mode === 'signup' && (
          <label>
            Name
            <input name="name" autoComplete="name" required />
          </label>
        )}
        <label>
          Email
          <input name="email" type="email" autoComplete="email" required autoFocus />
        </label>
        <label>
          Password
          <input name="password" type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={mode === 'signup' ? 8 : undefined} required />
        </label>
        {error && <div className="signin-error">{error}</div>}
        <button className="btn primary" disabled={busy} type="submit">
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </button>
        <button type="button" className="signin-switch" onClick={() => (setMode(mode === 'login' ? 'signup' : 'login'), setError(null))}>
          {mode === 'login' ? 'New here? Create an account' : 'Have an account? Sign in'}
        </button>
          </>
        )}
      </form>
    </div>
  );
}

interface ShareInfo {
  owner: { id: string; email: string; name: string } | null;
  link: 'none' | 'viewer' | 'editor';
  people: { id: string | null; email: string; name: string; role: 'editor' | 'viewer'; pending: boolean }[];
  role: 'owner' | 'editor' | 'viewer';
  url: string;
}

/** Invite people by email, set their roles and link access. */
export function ShareDialog() {
  const open = useApp('ui', () => app.ui.peopleOpen);
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const load = () =>
    fetch(`/api/files/${app.fileId}/share`)
      .then((r) => r.json())
      .then(setInfo)
      .catch(() => {});
  useEffect(() => {
    if (open) void load();
  }, [open]);
  if (!open) return null;
  const close = () => app.setUI({ peopleOpen: false });
  const isOwner = info?.role === 'owner' || (info && !info.owner);
  const change = async (body: unknown) => {
    setError(null);
    try {
      setInfo(await post(`/api/files/${app.fileId}/share`, body));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && close()}>
      <div className="modal" style={{ width: 'min(480px, calc(100vw - 32px))' }}>
        <header>
          <span>Share “{app.doc.get(app.doc.rootId)?.name}”</span>
          <button className="icon-btn" onClick={close}>
            {Icons.close}
          </button>
        </header>
        <div className="body">
          {isOwner && (
            <form
              className="row"
              style={{ gap: 6, marginBottom: 12 }}
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void change({ email: f.get('email'), role: f.get('role') });
                e.currentTarget.reset();
              }}
            >
              <div className="field">
                <input name="email" type="email" placeholder="Email address" required style={{ border: 0, background: 'transparent', outline: 'none', width: '100%' }} onKeyDown={(e) => e.stopPropagation()} />
              </div>
              <select name="role" defaultValue="editor" className="share-select">
                <option value="editor">can edit</option>
                <option value="viewer">can view</option>
              </select>
              <button className="btn primary" type="submit">
                Invite
              </button>
            </form>
          )}
          {error && <div className="signin-error">{error}</div>}
          <div className="share-people">
            {info?.owner && (
              <div className="share-person">
                <span className="avatar" style={{ background: '#1e1e1e' }}>{info.owner.name.slice(0, 1).toUpperCase()}</span>
                <span style={{ flex: 1 }}>
                  {info.owner.name} <span style={{ color: 'var(--text-2)' }}>{info.owner.email}</span>
                </span>
                <span style={{ color: 'var(--text-2)' }}>owner</span>
              </div>
            )}
            {info?.people.map((p) => (
              <div className="share-person" key={p.email}>
                <span className="avatar" style={{ background: p.pending ? '#b3b3b3' : '#0d99ff' }}>{p.name.slice(0, 1).toUpperCase()}</span>
                <span style={{ flex: 1 }}>
                  {p.pending ? p.email : p.name} <span style={{ color: 'var(--text-2)' }}>{p.pending ? 'invited · joins on sign-up' : p.email}</span>
                </span>
                {isOwner ? (
                  <select className="share-select" value={p.role} onChange={(e) => void change({ email: p.email, role: e.target.value === 'remove' ? null : e.target.value })}>
                    <option value="editor">can edit</option>
                    <option value="viewer">can view</option>
                    <option value="remove">remove</option>
                  </select>
                ) : (
                  <span style={{ color: 'var(--text-2)' }}>can {p.role === 'editor' ? 'edit' : 'view'}</span>
                )}
              </div>
            ))}
          </div>
          <h5>Anyone with the link</h5>
          <div className="row" style={{ gap: 6 }}>
            <select className="share-select" disabled={!isOwner} value={info?.link ?? 'none'} onChange={(e) => void change({ link: e.target.value })} style={{ flex: 1 }}>
              <option value="none">No access (only people invited)</option>
              <option value="viewer">Can view (signed-in people)</option>
              <option value="editor">Can edit (signed-in people)</option>
            </select>
            <button
              className="btn secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(info?.url ?? location.href);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Your avatar: account details, agent tokens, sign out. */
export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const user = app.account.user;
  if (!user) return null;
  return (
    <div style={{ position: 'relative' }}>
      <span className="avatar" style={{ background: '#1e1e1e', cursor: 'pointer' }} title={`${user.name} (you)`} onClick={() => setOpen(!open)}>
        {user.name.slice(0, 1).toUpperCase()}
      </span>
      {open && (
        <div className="menu" style={{ position: 'absolute', right: 0, top: 34 }} onMouseLeave={() => setOpen(false)}>
          <div style={{ padding: '6px 16px', color: '#aaa' }}>
            {user.name}
            <br />
            {user.email}
          </div>
          <hr />
          <button onClick={() => (setOpen(false), app.setUI({ shareOpen: true }))}>Agent tokens…</button>
          <button
            onClick={async () => {
              await post('/api/auth/logout', {});
              location.reload();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Create and list personal tokens agents (MCP, CLI) use to act as you. */
export function AgentTokens({ onCreated }: { onCreated: (token: string) => void }) {
  const [tokens, setTokens] = useState<{ id: string; label: string; createdAt: number }[]>([]);
  const [fresh, setFresh] = useState<string | null>(null);
  const load = () =>
    fetch('/api/auth/tokens')
      .then((r) => r.json())
      .then((t) => Array.isArray(t) && setTokens(t))
      .catch(() => {});
  useEffect(() => void load(), []);
  return (
    <>
      <h5>Agent tokens</h5>
      <div style={{ color: 'var(--text-2)', marginBottom: 6 }}>Agents act as you, with your access to files. Tokens are shown once; revoke one to cut its agent off.</div>
      {fresh && <div style={{ marginBottom: 6 }}>New token added to the commands below. Copy them now: it isn’t shown again.</div>}
      {tokens.map((t) => (
        <div className="row" key={t.id} style={{ justifyContent: 'space-between' }}>
          <span>
            {t.label} <span style={{ color: 'var(--text-2)' }}>· created {new Date(t.createdAt).toLocaleDateString()}</span>
          </span>
          <button className="btn secondary" onClick={async () => (await fetch(`/api/auth/tokens/${t.id}`, { method: 'DELETE' }), load())}>
            Revoke
          </button>
        </div>
      ))}
      <button
        className="btn primary"
        onClick={async () => {
          const r = await post('/api/auth/tokens', { label: `Agent token ${tokens.length + 1}` });
          setFresh(r.token);
          onCreated(r.token);
          void load();
        }}
      >
        Create token
      </button>
    </>
  );
}
